import React, { useState } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ListChecks, PencilSimple, Plus, Trash, ArrowUp, ArrowDown, CalendarBlank, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ScheduleReminderDialog } from './ScheduleReminderDialog';

const ROW_TYPES = [
  { type: 'heading', label: 'Section Heading' },
  { type: 'text_input', label: 'Text Row' },
  { type: 'long_text', label: 'Multi-line Row' },
  { type: 'date_input', label: 'Date Row' },
  { type: 'yes_no', label: 'Yes / No Row' },
  { type: 'checklist', label: 'Checkbox Row' },
  { type: 'dropdown', label: 'Dropdown Row' },
  { type: 'paragraph', label: 'Note' },
];

const EditFieldRow = ({ field, index, total, onChange, onRemove, onMove }) => {
  const cellBorder = 'border border-gray-300';
  const isContent = field.type === 'heading' || field.type === 'paragraph';

  return (
    <tr className="group relative">
      <td className={`${cellBorder} ${isContent ? 'bg-gray-100' : 'bg-gray-50'} px-3 py-2 ${isContent ? '' : 'w-[50%]'}`} colSpan={isContent ? 2 : 1}>
        <input
          value={isContent ? (field.content || '') : (field.label || '')}
          onChange={(e) => onChange({ ...field, [isContent ? 'content' : 'label']: e.target.value })}
          placeholder={isContent ? (field.type === 'heading' ? 'Section heading...' : 'Note text...') : 'Field label...'}
          className={`w-full bg-transparent text-sm focus:outline-none ${isContent ? (field.type === 'heading' ? 'font-bold uppercase tracking-wide' : 'italic text-gray-600') : 'font-medium text-gray-800'}`}
          data-testid={`edit-field-label-${index}`}
        />
      </td>
      {!isContent && (
        <td className={`${cellBorder} px-3 py-2`}>
          {field.type === 'text_input' || field.type === 'long_text' || field.type === 'date_input' ? (
            <input
              value={field.prefill || ''}
              onChange={(e) => onChange({ ...field, prefill: e.target.value })}
              placeholder={field.type === 'date_input' ? 'DD/MM/YYYY' : 'Pre-fill value (optional)...'}
              className="w-full bg-transparent text-sm text-gray-500 focus:outline-none"
              data-testid={`edit-field-prefill-${index}`}
            />
          ) : field.type === 'yes_no' ? (
            <span className="text-xs text-gray-400">Yes / No / Attached / N/A</span>
          ) : field.type === 'checklist' ? (
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 border-2 border-gray-300 rounded inline-block" />
              <span className="text-xs text-gray-400">Checkbox</span>
            </div>
          ) : field.type === 'dropdown' ? (
            <input
              value={typeof field.options === 'string' ? field.options : (Array.isArray(field.options) ? field.options.join(', ') : '')}
              onChange={(e) => onChange({ ...field, options: e.target.value })}
              placeholder="Options (comma separated)"
              className="w-full bg-transparent text-sm text-gray-500 focus:outline-none"
              data-testid={`edit-field-options-${index}`}
            />
          ) : null}
        </td>
      )}
      <td className={`${cellBorder} px-1 py-1 w-[80px] text-center`}>
        <div className="flex items-center justify-center gap-0.5">
          {index > 0 && <button type="button" onClick={() => onMove(index, index - 1)} className="p-0.5 hover:bg-gray-100 rounded"><ArrowUp className="w-3 h-3 text-gray-400" /></button>}
          {index < total - 1 && <button type="button" onClick={() => onMove(index, index + 1)} className="p-0.5 hover:bg-gray-100 rounded"><ArrowDown className="w-3 h-3 text-gray-400" /></button>}
          <button type="button" onClick={() => onRemove(index)} className="p-0.5 hover:bg-red-50 rounded"><Trash className="w-3 h-3 text-red-400" /></button>
        </div>
      </td>
    </tr>
  );
};

export const ClientFormsTab = ({
  clientId, formTemplates, formAssignments, selectedFormTemplate,
  onSelectTemplate, onRefresh,
}) => {
  const submittedForms = formAssignments.filter((f) => f.status === 'submitted');
  const [editingForm, setEditingForm] = useState(null);
  const [editFields, setEditFields] = useState([]);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const openEditor = (form) => {
    setEditingForm(form);
    setEditFields(JSON.parse(JSON.stringify(form.fields || [])));
    setEditName(form.form_name || '');
  };

  const handleAssignForm = async () => {
    if (!selectedFormTemplate) return;
    try {
      const { data } = await api.post(`/clients/${clientId}/assign-form`, { template_id: selectedFormTemplate });
      toast.success('Form assigned — customize it for this client');
      onSelectTemplate('');
      onRefresh();
      openEditor(data);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const updateField = (index, updated) => {
    const f = [...editFields]; f[index] = updated; setEditFields(f);
  };
  const removeField = (index) => setEditFields(editFields.filter((_, i) => i !== index));
  const moveField = (from, to) => {
    const f = [...editFields]; const [m] = f.splice(from, 1); f.splice(to, 0, m); setEditFields(f);
  };
  const addField = (type) => {
    const id = `field_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setEditFields([...editFields, { id, type, ...(type === 'heading' || type === 'paragraph' ? { content: '' } : { label: '' }), ...(type === 'dropdown' ? { options: '' } : {}) }]);
  };

  const handleSaveEdits = async () => {
    if (!editingForm) return;
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/form-assignments/${editingForm.id}`, {
        form_name: editName,
        fields: editFields,
      });
      toast.success('Form customized for this client');
      setEditingForm(null);
      onRefresh();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };

  const handleDownloadPdf = async (form) => {
    setDownloading(form.id);
    try {
      const response = await api.post(`/clients/${clientId}/form-assignments/${form.id}/download-pdf`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${(form.form_name || 'form').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Form PDF downloaded');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setDownloading(null); }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="heading-4 flex items-center gap-2"><ListChecks className="w-5 h-5" /> Online Forms</CardTitle>
          <CardDescription>Assign fillable forms for the client to complete online</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1 space-y-1">
              <Label className="text-sm">Select Form Template</Label>
              <Select value={selectedFormTemplate} onValueChange={onSelectTemplate}>
                <SelectTrigger data-testid="form-template-select"><SelectValue placeholder="Choose a form template" /></SelectTrigger>
                <SelectContent>
                  {formTemplates.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAssignForm} disabled={!selectedFormTemplate} data-testid="assign-form-btn">Assign to Client</Button>
          </div>
          {formTemplates.length === 0 && (
            <p className="text-sm text-muted-foreground">No form templates yet. Go to the <strong>Forms</strong> page in the sidebar to create one first.</p>
          )}
          {formAssignments.length > 0 && (
            <div className="space-y-3 pt-2">
              <h4 className="text-sm font-medium text-muted-foreground">Assigned Forms</h4>
              {formAssignments.map((form) => (
                <div key={form.id} className="flex items-center justify-between p-3 border rounded-md" data-testid={`form-assignment-${form.id}`}>
                  <div>
                    <p className="font-medium text-sm">{form.form_name}</p>
                    <p className="text-xs text-muted-foreground">
                      Assigned {new Date(form.assigned_at).toLocaleDateString()}
                      {form.submitted_at && ` | Submitted ${new Date(form.submitted_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={form.status === 'submitted' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}>
                      {form.status === 'submitted' ? 'Submitted' : 'Pending'}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => openEditor(form)} data-testid={`edit-form-${form.id}`}>
                      <PencilSimple className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(form)} disabled={downloading === form.id} data-testid={`download-form-${form.id}`}>
                      <DownloadSimple className="w-3.5 h-3.5 mr-1" /> {downloading === form.id ? 'Downloading...' : 'Download PDF'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setScheduleForm(form); setScheduleOpen(true); }} data-testid={`schedule-form-${form.id}`}>
                      <CalendarBlank className="w-3.5 h-3.5 mr-1" /> Schedule
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Form Assignment Dialog */}
      <Dialog open={!!editingForm} onOpenChange={(open) => { if (!open) setEditingForm(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Customize Form for this Client</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1">
              <Label className="text-sm">Form Name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-form-name" />
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="bg-white border border-gray-300 shadow-sm max-w-[720px] mx-auto">
                <div className="border-b border-gray-300 px-4 py-3 text-center">
                  <p className="font-bold text-sm text-gray-800">{editName || 'Form'}</p>
                  <p className="text-[10px] text-gray-400 mt-1">Applegrow Financial Advisors</p>
                </div>
                {editFields.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">No fields yet — add rows below</div>
                ) : (
                  <table className="w-full border-collapse text-sm" data-testid="edit-form-table">
                    <tbody>
                      {editFields.map((field, idx) => (
                        <EditFieldRow key={field.id || idx} field={field} index={idx} total={editFields.length} onChange={(u) => updateField(idx, u)} onRemove={removeField} onMove={moveField} />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {ROW_TYPES.map((rt) => (
                <Button key={rt.type} type="button" variant="outline" size="sm" className="text-xs" onClick={() => addField(rt.type)} data-testid={`add-edit-row-${rt.type}`}>
                  <Plus className="w-3 h-3 mr-1" /> {rt.label}
                </Button>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <Button variant="outline" onClick={() => setEditingForm(null)}>Cancel</Button>
              <Button onClick={handleSaveEdits} disabled={saving} data-testid="save-form-edits-btn">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submitted form responses */}
      {submittedForms.map((form) => (
        <Card key={form.id} className="border border-border">
          <CardHeader className="pb-3">
            <CardTitle className="heading-4">{form.form_name} — Responses</CardTitle>
            <p className="text-xs text-muted-foreground">Submitted {form.submitted_at ? new Date(form.submitted_at).toLocaleDateString() : ''}</p>
          </CardHeader>
          <CardContent className="p-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="bg-white border border-slate-300 shadow-sm max-w-[660px] mx-auto">
                <div className="border-b border-slate-300 px-4 py-3 text-center">
                  <p className="font-bold text-sm text-slate-800">{form.form_name}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Applegrow Financial Advisors</p>
                </div>
                <div className="px-3 py-2">
                  <table className="w-full border-collapse text-sm">
                    <tbody>
                      {(form.fields || []).map((field) => {
                        if (field.type === 'heading') return (
                          <tr key={field.id}><td colSpan={2} className="bg-slate-100 border border-slate-300 px-3 py-2 font-bold text-xs uppercase tracking-wide text-slate-700">{field.content || field.label}</td></tr>
                        );
                        if (field.type === 'paragraph') return (
                          <tr key={field.id}><td colSpan={2} className="border border-slate-300 px-3 py-2 text-xs text-slate-500 italic">{field.content}</td></tr>
                        );
                        if (field.type === 'checklist') {
                          const checked = form.responses?.[field.id] || {};
                          return (
                            <React.Fragment key={field.id}>
                              <tr><td colSpan={2} className="bg-slate-50 border border-slate-300 px-3 py-2 font-semibold text-xs text-slate-700">{field.label}</td></tr>
                              {(field.items || []).map((item, idx) => (
                                <tr key={`${field.id}-${idx}`}><td colSpan={2} className="border border-slate-300 px-3 py-1"><span className="text-xs">{checked[idx] || checked[String(idx)] ? 'Yes' : 'No'} — {item}</span></td></tr>
                              ))}
                            </React.Fragment>
                          );
                        }
                        const resp = form.responses?.[field.id];
                        return (
                          <tr key={field.id}>
                            <td className="border border-slate-300 px-3 py-2 font-medium text-xs w-[40%] bg-slate-50 text-slate-700">{field.label}</td>
                            <td className="border border-slate-300 px-3 py-2 text-xs">{resp != null && resp !== '' ? String(resp) : <span className="text-slate-400">—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Schedule Reminder Dialog */}
      {scheduleForm && (
        <ScheduleReminderDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          clientId={clientId}
          type="form"
          templateId={scheduleForm.template_id}
          templateName={scheduleForm.form_name}
          initialFields={scheduleForm.fields}
          onCreated={onRefresh}
        />
      )}
    </div>
  );
};
